import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

const RATES = [["23", "23%"], ["8", "8%"], ["5", "5%"], ["0", "0%"], ["zw", "zw."], ["np", "np."]];
const iso = (x) => x.toLocaleDateString("sv-SE");
const today = () => iso(new Date());
const addDays = (d, n) => { const [y, m, dd] = d.split("-").map(Number); return iso(new Date(y, m - 1, dd + n)); };
const isPL = (c) => ["poland", "polska", "pl"].includes((c || "").trim().toLowerCase());
const money = (v) => (Math.round(v * 100) / 100).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sel = "h-9 border border-border rounded-md px-2 bg-white text-sm";

const blank = (days) => ({ kind: "proforma", invoice_type: "VAT", client_id: null, buyer: { name: "", address: "", country: "", nip: "", email: "" }, issue_date: today(), sale_date: today(),
  due_date: addDays(today(), days), currency: "PLN", items: [{ name: "", qty: 1, unit: "usł.", unit_net: 0, vat_rate: "23" }], notes: "", exemption_basis: "" });

const fromClient = (c, f) => {
  const rate = isPL(c.country || (c.region === "Poland" ? "Poland" : "")) ? "23" : "np";
  const currency = rate === "np" && f.currency === "PLN" ? "EUR" : rate === "23" ? "PLN" : f.currency;
  return { ...f, currency, client_id: c.client_id, buyer: { name: c.company_name || "", address: c.address || "", country: c.country || (c.region === "Poland" ? "Poland" : ""), nip: c.nip || "", email: c.email || "" },
    items: f.items.map((i) => ({ ...i, vat_rate: rate })) };
};

export function ItemRow({ it, i, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...it, [k]: v });
  return (
    <div className="grid grid-cols-12 gap-1.5 items-center" data-testid={`inv-item-${i}`}>
      <Input className="col-span-12 sm:col-span-5 h-9" placeholder="Nazwa usługi" value={it.name} onChange={(e) => set("name", e.target.value)} data-testid={`inv-item-name-${i}`} />
      <Input className="col-span-2 sm:col-span-1 h-9 px-2" type="number" min={0} step="any" value={it.qty} onChange={(e) => set("qty", e.target.value)} data-testid={`inv-item-qty-${i}`} />
      <Input className="col-span-2 sm:col-span-1 h-9 px-2" value={it.unit} onChange={(e) => set("unit", e.target.value)} data-testid={`inv-item-unit-${i}`} />
      <Input className="col-span-3 sm:col-span-2 h-9 px-2" type="number" min={0} step="0.01" placeholder="Cena netto" value={it.unit_net} onChange={(e) => set("unit_net", e.target.value)} data-testid={`inv-item-price-${i}`} />
      <select className={`${sel} col-span-3 sm:col-span-1 px-1`} value={it.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} data-testid={`inv-item-vat-${i}`}>
        {RATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <span className="hidden sm:block col-span-1 text-right text-xs tabular-nums">{money((+it.qty || 0) * (+it.unit_net || 0))}</span>
      <Button variant="ghost" size="sm" className="col-span-2 sm:col-span-1 h-9 px-0" onClick={onRemove} data-testid={`inv-item-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

function Totals({ items, currency }) {
  const t = useMemo(() => items.reduce((a, i) => {
    const net = Math.round((+i.qty || 0) * (+i.unit_net || 0) * 100) / 100;
    const vat = /^\d+$/.test(i.vat_rate) ? Math.round(net * +i.vat_rate) / 100 : 0;
    return { net: a.net + net, vat: a.vat + vat };
  }, { net: 0, vat: 0 }), [items]);
  return (
    <div className="text-sm text-right font-[Manrope]" data-testid="inv-totals">
      Netto {money(t.net)} · VAT {money(t.vat)} · <strong className="text-[#193962]">Brutto {money(t.net + t.vat)} {currency}</strong>
    </div>
  );
}

export const InvoiceEditor = ({ open, invoice, presetClientId, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [days, setDays] = useState(14);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(null);
    setLoadErr(false);
    Promise.all([api.get("/clients"), api.get("/invoice-settings")]).then(([c, s]) => {
      setClients(c.data);
      setDays(s.data.payment_days);
      setSavedId(invoice?.invoice_id || null);
      const base = invoice ? { ...invoice } : blank(s.data.payment_days);
      const pre = !invoice && presetClientId && c.data.find((x) => x.client_id === presetClientId);
      setF(pre ? fromClient(pre, base) : base);
    }).catch(() => { setLoadErr(true); toast.error("Nie udało się wczytać danych"); });
  }, [open, invoice, presetClientId]);

  if (!open) return null;
  const setB = (k, v) => setF((x) => ({ ...x, buyer: { ...x.buyer, [k]: v } }));
  const setItem = (i, v) => setF((x) => ({ ...x, items: x.items.map((it, j) => (j === i ? v : it)) }));
  const payload = () => ({ ...f, items: f.items.map((i) => ({ ...i, qty: +i.qty || 0, unit_net: +i.unit_net || 0 })) });

  const submit = async (issue) => {
    setBusy(true);
    try {
      const r = savedId ? await api.put(`/invoices/${savedId}`, payload()) : await api.post("/invoices", payload());
      setSavedId(r.data.invoice_id);
      if (issue) {
        const done = await api.post(`/invoices/${r.data.invoice_id}/issue`);
        toast.success(`Wystawiono ${done.data.number}`);
      } else toast.success("Zapisano szkic");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || "Błąd zapisu"); onSaved(false); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto" data-testid="invoice-editor">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">{savedId ? "Edytuj szkic" : "Nowy dokument"}</DialogTitle>
          <DialogDescription>Sprzedawca: Toren PATH sp. z o.o., Kraków · NIP 9452337470</DialogDescription>
        </DialogHeader>
        {!f ? <p className="text-sm text-muted-foreground" data-testid="inv-loading">{loadErr ? "Nie udało się wczytać klientów lub ustawień. Zamknij okno i spróbuj ponownie." : "Wczytywanie…"}</p> : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center" data-testid="inv-kind">
              {[["proforma", "Proforma (domyślnie)"], ["invoice", "Faktura VAT (KSeF)"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} data-testid={`inv-kind-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${f.kind === k ? "bg-[#193962] text-white border-[#193962]" : "bg-white border-border"}`}>{l}</button>
              ))}
              {f.kind === "invoice" && (
                <select className={sel} value={f.invoice_type || "VAT"} onChange={(e) => setF({ ...f, invoice_type: e.target.value })} data-testid="inv-invoice-type">
                  <option value="VAT">Faktura końcowa (VAT)</option><option value="ZAL">Faktura zaliczkowa (ZAL)</option>
                </select>
              )}
              <span className="text-[11px] text-muted-foreground">{f.kind === "proforma" ? "Proforma nie trafia do KSeF; po opłaceniu zamienisz ją na fakturę." : "Faktura otrzyma plik FA(3) i zostanie wysłana do KSeF."}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Klient z CRM</Label>
                <select className={`${sel} w-full`} value={f.client_id || ""} data-testid="inv-client-select"
                  onChange={(e) => { const c = clients.find((x) => x.client_id === e.target.value); setF(c ? fromClient(c, f) : { ...f, client_id: null }); }}>
                  <option value="">— wpisz nabywcę ręcznie —</option>
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.company_name}</option>)}
                </select>
                <Input placeholder="Nazwa nabywcy" value={f.buyer.name} onChange={(e) => setB("name", e.target.value)} data-testid="inv-buyer-name" />
                <Textarea rows={2} placeholder="Adres" value={f.buyer.address} onChange={(e) => setB("address", e.target.value)} data-testid="inv-buyer-address" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Kraj (np. Poland, Kenya)" value={f.buyer.country} onChange={(e) => setB("country", e.target.value)} data-testid="inv-buyer-country" />
                  <Input placeholder={isPL(f.buyer.country) ? "NIP" : "Nr podatkowy (opcjonalnie)"} value={f.buyer.nip} onChange={(e) => setB("nip", e.target.value)} data-testid="inv-buyer-nip" />
                </div>
                <Input type="email" placeholder="E-mail nabywcy (wysyłka i przypomnienia)" value={f.buyer.email || ""} onChange={(e) => setB("email", e.target.value)} data-testid="inv-buyer-email" />
              </div>
              <div className="grid grid-cols-2 gap-2 content-start">
                <div><Label>Data wystawienia</Label><Input type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value, due_date: e.target.value ? addDays(e.target.value, days) : f.due_date })} data-testid="inv-issue-date" /></div>
                <div><Label>{f.kind === "invoice" && f.invoice_type === "ZAL" ? "Data otrzymania zapłaty" : "Data sprzedaży"}</Label><Input type="date" value={f.sale_date} onChange={(e) => setF({ ...f, sale_date: e.target.value })} data-testid="inv-sale-date" /></div>
                <div><Label>Termin płatności</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="inv-due-date" /></div>
                <div><Label>Waluta</Label>
                  <select className={`${sel} w-full`} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} data-testid="inv-currency">
                    {["PLN", "EUR", "USD"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                {!isPL(f.buyer.country) && f.buyer.country && <p className="col-span-2 text-[11px] text-amber-700 bg-amber-50 rounded p-2">Nabywca zagraniczny: domyślnie „np.” z adnotacją „Odwrotne obciążenie”. Potwierdź traktowanie VAT z księgową.</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pozycje (cena jednostkowa netto)</Label>
              {f.items.map((it, i) => <ItemRow key={i} it={it} i={i} onChange={(v) => setItem(i, v)} onRemove={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} />)}
              <Button variant="outline" size="sm" data-testid="inv-add-item"
                onClick={() => setF({ ...f, items: [...f.items, { name: "", qty: 1, unit: "usł.", unit_net: 0, vat_rate: f.items[0]?.vat_rate || "23" }] })}>
                <Plus className="h-3.5 w-3.5 mr-1" />Dodaj pozycję
              </Button>
              <Totals items={f.items} currency={f.currency} />
            </div>
            {f.items.some((i) => i.vat_rate === "zw") && <Input placeholder="Podstawa zwolnienia z VAT (np. art. 43 ust. 1 pkt 37 ustawy o VAT)" value={f.exemption_basis || ""} onChange={(e) => setF({ ...f, exemption_basis: e.target.value })} data-testid="inv-exemption-basis" />}
            <Textarea rows={2} placeholder="Uwagi na fakturze (opcjonalnie)" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="inv-notes" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" disabled={busy} onClick={() => submit(false)} data-testid="inv-save-draft">Zapisz szkic</Button>
              <Button disabled={busy} onClick={() => submit(true)} data-testid="inv-issue-btn" className="bg-[#193962] text-white">{busy ? "Zapisywanie…" : f.kind === "proforma" ? "Wystaw proformę" : "Wystaw fakturę"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
